import { scopedKeyHelperModule } from '../../helpers/scoped-key/scoped-key';
import { warnOnChangesComponentModule } from '../../components/forms/warn-on-changes/warn-on-changes';
import { parseDates } from '../../utils/date-utils';
import * as ObjectUtils from '../../utils/object-utils/object-utils';
import { storageServiceModule } from '../storage-service/storage-service';
import { appConfig } from '../../constants/app-config';
import { remove } from '../../utils/array-utils';
import { getUser } from '../../utils/account-utils/account-utils';

// not constant so we can append the "app" id for unique data per "app"
let FORM_DATA_KEY = 'FORM_DATA';
const ALWAYS_IGNORE = [
  'createdAt',
  'publishedAt',
  'updatedAt',
  'archivedAt',
  'digest',
];
const FORM_DATA_CACHE_EXPIRE = 5 * 60 * 1000; // expire stale caches after 5 minutes

export const formDataServiceModule = angular
  .module('Q.services.form-data-service', [
    storageServiceModule,
    scopedKeyHelperModule,
    warnOnChangesComponentModule,
  ])

  .run(function ($localStorage, $rootScope) {
    FORM_DATA_KEY += '_' + appConfig.appId;
    $rootScope.$on('app.logout', function () {
      delete $localStorage[FORM_DATA_KEY];
      $localStorage.$apply();
    });
  })

  .factory(
    'formDataService',
    function (
      scopedKey,
      warnOnChangesService,
      $localStorage,
      $window,
      $state,
      $injector,
      $timeout,
    ) {
      // The cache only stores the single, most recently used form data.
      // If we visit a different form, we'll overwrite it.
      // The cache also stores a timestamp, and if we exceed the cache time since it was last used, we disregard
      // updates.
      let cache = null;

      function initCache(force = false) {
        if (
          force ||
          !$localStorage[FORM_DATA_KEY] ||
          !$localStorage[FORM_DATA_KEY].key
        ) {
          $localStorage[FORM_DATA_KEY] = cache = {};
        } else {
          cache = $localStorage[FORM_DATA_KEY];
        }
        const user = getUser($injector);
        if (user) {
          const id = user.id;
          if (cache._id !== id) {
            $localStorage[FORM_DATA_KEY] = cache = { _id: id };
          }
        }
      }

      function setCacheExpiration() {
        if (cache) {
          cache.stored = Date.now();
        }
      }

      return {
        create,
        reset,
      };

      /**
       * Parameters are provided as an object
       * @param options - Options
       * @param {*} options.originalData - Data that will be used on the form
       * @param {Function} options.handleSave - A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog. Can also be set on the return object.
       * @param {string} options.id - Optional ID, defaults to data.id
       * @param {Scope} options.scope - Optional scope for the key to store this object, defaults to 'state' @see scopedKeys helper
       * @param {Array<string>} options.ignoredProperties - A list of properties to ignore on the data object
       * @param {boolean} options.warnOnUnload - If you don't want to warn the user if they leave this scope or unload the page, pass in false
       * @param {string} options.unloadMessage - Override default message to show if the page changes
       * @param {string} options.routeChangeMessage - Override message to show if the route changes
       * @param {string} options.routeChangeWithSaveMessage-  Override message to show if the route changes and you provide an handleSave method
       * @param {boolean} options.storeChanges - If set to false, the service will not store a copy of the changes in $localStorage (useful for autosave forms)
       * @param {boolean} options.autoRestoreCache - If true, automatically restores the cache if we have one
       * @param {boolean} options.resetCache - If true, clears any previous cached value
       * @param {Function} options.isDiff - If provided, used when diffing for hasChanges. Can also be set on the return object
       * @returns {{}}
       */
      function create(options) {
        let {
          originalData,
          handleSave,
          id = originalData.id,
          scope = 'state',
          ignoredProperties = [],
          warnOnUnload = true,
          storeChanges = true,
          autoRestoreCache = false,
          resetCache = false,
        } = options;

        const key = _getKey(scope, id);
        const data = Object.create(originalData); // our active form data
        const dataSyncCopy = Object.create(originalData); // a copy of the original data, for 3-way diffing
        let cacheCopy = null; // backup of copy from the cache

        const ignorePropertyRegex = new RegExp(
          [].concat(ignoredProperties, ALWAYS_IGNORE).join('|'),
        );
        const isTrackedProperty = (k) => {
          let tracked = false;
          if (
            k &&
            !ignorePropertyRegex.test(k) &&
            originalData.propertyIsEnumerable(k)
          ) {
            const pd = Object.getOwnPropertyDescriptor(originalData, k);
            // ignore any properties that are not writeable or settable, or functions
            if (
              !pd ||
              (pd.configurable &&
                (pd.writable || pd.set) &&
                !angular.isFunction(originalData[k]))
            ) {
              tracked = true;
            }
          }
          return tracked;
        };

        const fd = {
          forms: [], // array of associated forms
          data,
          originalData,
          handleSave,
          updateData,
          syncUnchanged,
          reset,
          hasCache,
          restoreCache,
          initForm,
          finish,
          cancel,
          close,
          hasChanges,
          getChanges,
          isDiff: options.isDiff || ObjectUtils.isDiff,
        };

        reset();

        if (storeChanges) {
          initCache();
          if (
            !resetCache &&
            cache.key === key &&
            cache.data &&
            cache.stored > Date.now() - FORM_DATA_CACHE_EXPIRE
          ) {
            // the cache copy is stored on FD for debugging purposes
            fd.$cacheCopy = cacheCopy = parseDates(angular.copy(cache.data));
            if (autoRestoreCache) restoreCache();
          }
          cache.key = key;
          cache.data = data;
          setCacheExpiration();
        }

        if (warnOnUnload) {
          warnOnChangesService.enable(
            angular.extend({}, options, {
              hasChanges() {
                // enables overriding hasChanges
                return fd.hasChanges();
              },
              ignoreRouteChange(evt, toState, toParams) {
                const fromScope = scopedKey(scope);
                const toScope = scopedKey(
                  scope,
                  toState.name,
                  toParams,
                  $state.href(toState.name, toParams),
                );

                return fromScope === toScope;
              },
              handleSave() {
                return options.handleSave();
              },
              handleFinish() {
                return fd.finish();
              },
            }),
          );
        }

        return fd;

        //// public methods

        function initForm(scope, form) {
          fd.forms.push(form);
          // start listening for model changes
          scope.$on('socket:model.changed', _syncUnchangedDebounce);
          scope.$on('model.changed', () => _syncUnchangedDebounce);
          // cleanup for multiple forms
          scope.$on('$destroy', () => {
            remove(fd.forms, form);
          });
        }

        /**
         * Replace the current "original data" with new data.  Does NOT reset the form data,
         * to do so call reset() after this method.
         *
         * If we want to retain the current changed form data, we can use syncUnchanged(),
         * which only resets fields that have have not been changed by the user.
         *
         * @param newData
         */
        function updateData(newData) {
          fd.originalData = originalData = newData;
        }

        /**
         * Gets all the keys in the original data, minus
         * the ignored properties
         * @returns {Array.<T>}
         */
        function dataKeys() {
          return Object.keys(originalData).filter(isTrackedProperty);
        }

        /**
         * Resets the form data to the original object
         */
        function reset(k) {
          if (k) {
            data[k] = angular.copy(originalData[k]);
            // this copy tracks 3-way changes
            dataSyncCopy[k] = angular.copy(originalData[k]);
          } else {
            ObjectUtils.empty(data);
            ObjectUtils.empty(dataSyncCopy);
            dataKeys().forEach(reset);
          }

          setCacheExpiration();
        }

        /**
         * Returns true if there is a cached copy
         * @returns {boolean}
         */
        function hasCache() {
          return !!cacheCopy && hasChanges(null, cacheCopy);
        }

        /**
         * Restores the cache copy (if there is one)
         */
        function restoreCache() {
          if (!cacheCopy) return;

          angular.forEach(cacheCopy, function (v, k) {
            if (isTrackedProperty(k)) {
              data[k] = v;
            }
          });

          if (fd.afterRestoreCache) {
            fd.afterRestoreCache();
          }

          // Should we ever enable re-restoring cache changes?
          cacheCopy = null;
          setCacheExpiration();
        }

        /**
         * Synchronizes all unchanged values back from the original data object
         */
        function syncUnchanged(k) {
          if (!k || !angular.isString(k)) {
            dataKeys().forEach(syncUnchanged);
          } else if (
            angular.equals(data[k], dataSyncCopy[k]) &&
            !angular.equals(data[k], originalData[k])
          ) {
            data[k] = angular.copy(originalData[k]);
            dataSyncCopy[k] = angular.copy(originalData[k]);
          }
          setCacheExpiration();
        }

        function _syncUnchangedDebounce() {
          if (_syncUnchangedDebounce.timer) return;
          _syncUnchangedDebounce.timer = $timeout(() => {
            _syncUnchangedDebounce.timer = null;
            syncUnchanged();
          }, 10);
        }

        /**
         * Used to end working with the form via user action.
         * This method will:
         *     - clear the window beforeunload handler if used
         *     - clear the state change listener if used
         *     - Remove the saved data from the cache
         */
        function finish() {
          if (cache) {
            initCache(true);
          }
          _stopListening();
        }

        /**
         * Used to stop listening for changes and terminate this form data object.
         * Only necessary when switching between routes that are editing the same object.
         */
        function cancel() {
          _stopListening();
        }

        /**
         * Closes this form data. If there are no changes (i.e., it's saved), we also clear the cache
         */
        function close() {
          if (hasChanges()) {
            cancel();
          } else {
            finish();
          }
        }

        /**
         * Checks to see if there are differences between the original object
         * and the current one.
         * @returns {boolean}
         */
        function hasChanges(k, compareData) {
          compareData = compareData || originalData;
          let changes = false;
          if (angular.isString(k)) {
            changes =
              isTrackedProperty(k) && !fd.isDiff(data[k], compareData[k], k);
          } else {
            changes = Object.keys(data).some(
              (k) =>
                isTrackedProperty(k) && fd.isDiff(compareData[k], data[k], k),
            );
          }
          if (changes) {
            setCacheExpiration();
          }
          return changes;
        }

        /**
         * Simple method to return top-level changes between the data object and the
         * original object. This method considers the whole sub-object changed if any
         * part of it changed
         */
        function getChanges(compareData) {
          const changes = ObjectUtils.diff(
            compareData || originalData,
            data,
            (k) => !isTrackedProperty(k),
          );
          if (!changes.$empty) {
            setCacheExpiration();
          }
          return changes;
        }

        //// private

        function _stopListening() {
          if (warnOnUnload) {
            warnOnChangesService.disable();
          }
        }
      }

      function reset() {
        delete $localStorage[FORM_DATA_KEY];
        $localStorage.$apply();
      }

      //// private functions

      function _getKey(scope, id) {
        return scopedKey.activeState(scope) + '(' + id + ')';
      }
    },
  );
